import { Link } from "react-router-dom";
import { ArrowRight, BookOpen, Check, Landmark } from "lucide-react";
import ComplianceStrip from "../components/ComplianceStrip";
import GuideMeta from "../components/GuideMeta";
import ConveyancingCTA from "../components/ConveyancingCTA";
import SurveyQuoteCTA from "../components/SurveyQuoteCTA";
import BrokerCTA from "../components/BrokerCTA";
import { CashbackPromptCard } from "../components/CashbackPrompt";
import { cashbackPromotionEnabled } from "../config/promotions";
import Seo from "../components/Seo";
import { track } from "../lib/api";
import { FTB_SECTIONS as sections } from "../content/site";

const TOP_LINKS = [
  { label: "Conveyancing", id: "conveyancing" },
  { label: "Surveys (RICS Level 1–3)", id: "surveys" },
  { label: "AIP & affordability", id: "aip" },
  { label: "Stamp Duty", id: "costs" },
  { label: "Schemes & LISAs", id: "schemes" },
];

const Pill = ({ children }) => (
  <span className="inline-flex items-center gap-1.5 text-[11px] font-medium uppercase tracking-widest text-[#0F766E] bg-[#0F766E]/8 border border-[#0F766E]/15 rounded-full px-2.5 py-1">
    {children}
  </span>
);

const Bullet = ({ children }) => (
  <li className="flex gap-3 text-[15px] text-[#475569] leading-relaxed">
    <Check className="w-4 h-4 mt-1 shrink-0 text-[#10B981]" />
    <span>{children}</span>
  </li>
);

const SubItem = ({ h, b }) => (
  <div className="rounded-xl bg-[#F7FAF9] border border-[#DCE7E3] p-5">
    <div className="font-sora text-base font-semibold text-[#0B1220]">{h}</div>
    <p className="mt-1.5 text-sm text-[#475569] leading-relaxed">{b}</p>
  </div>
);

const AffiliateSlot = ({ a }) => {
  // Conveyancer affiliate is a live partner referral — render the fully clickable amber CTA card.
  if (a.id === "conveyancer") {
    return <ConveyancingCTA source="guide_ftb_conveyancing" variant="clickable-card" />;
  }
  // Surveyor affiliate — matching companion amber CTA card.
  if (a.id === "surveyor") {
    return <SurveyQuoteCTA source="guide_ftb_survey" variant="clickable-card" />;
  }
  // Broker route — teal companion CTA card.
  if (a.id === "broker") {
    return <BrokerCTA source="guide_ftb_broker" variant="clickable-card" className="mt-6" />;
  }
  // Stamp duty calculator — free internal tool card (brand teal).
  if (a.id === "stampduty") {
    return (
      <Link
        to={a.href}
        onClick={() => track("cta_clicked", { cta_location: "guide_ftb_costs", cta_text: a.cta, destination: "/stamp-duty-calculator" })}
        className="block group mt-6 rounded-2xl border border-[#0F766E]/30 bg-[#0F766E]/5 p-5 hover:shadow-md transition-all"
        data-testid="ftb-stamp-duty-card"
      >
        <div className="flex items-start gap-4">
          <span className="mt-0.5 w-10 h-10 rounded-xl grid place-items-center shrink-0 bg-[#0F766E]/10 text-[#0F766E]">
            <Landmark className="w-5 h-5" />
          </span>
          <div className="flex-1">
            <div className="text-[11px] uppercase tracking-widest font-semibold text-[#0F766E]">{a.label}</div>
            <h3 className="font-sora text-xl font-semibold text-[#0B1220] mt-1">{a.title}</h3>
            <p className="mt-2 text-sm text-[#475569] leading-relaxed">{a.desc}</p>
            <span className="mt-4 inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-sm font-semibold shadow-sm bg-[#0F766E] text-white group-hover:bg-[#0B5F58] transition-colors">
              {a.cta} <ArrowRight className="w-4 h-4" />
            </span>
          </div>
        </div>
      </Link>
    );
  }
  // No other affiliate slots exist; anything unknown renders nothing.
  return null;
};

const Section = ({ s }) => (
  <article id={s.id} className="mp-card p-6 sm:p-8 scroll-mt-24">
    <h2 className="font-sora text-2xl sm:text-[1.65rem] font-semibold text-[#0B1220] tracking-tight">{s.h}</h2>
    {s.intro && (
      <p className="mt-3 text-[#475569] leading-relaxed text-[15px]">{s.intro}</p>
    )}
    {s.paragraphs?.map((p) => (
      <p key={p.slice(0, 40)} className="mt-3 text-[#475569] leading-relaxed text-[15px]">{p}</p>
    ))}
    {s.bullets && (
      <ul className="mt-5 space-y-2.5">
        {s.bullets.map((b) => <Bullet key={b.slice(0, 40)}>{b}</Bullet>)}
      </ul>
    )}
    {s.items && (
      <div className="mt-5 grid gap-3 sm:grid-cols-2">
        {s.items.map((it) => <SubItem key={it.h} {...it} />)}
      </div>
    )}
    {s.affiliate && <AffiliateSlot a={s.affiliate} />}
  </article>
);

const FirstTimeBuyer = () => (
  <div className="mp-container py-14">
    <Seo
      title="First-Time Buyer Guide — the UK home buying process explained"
      description="The complete UK home buying journey for first-time buyers. Deposits, AIP, affordability, conveyancing, RICS surveys (Level 1, 2, 3), Stamp Duty, exchange and completion — in plain English."
      path="/first-time-buyer-guide"
      type="article"
    />
    <div className="max-w-3xl">
      <Pill><BookOpen className="w-3 h-3" /> Educational guide</Pill>
      <h1 className="font-sora mt-4 text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-[#0B1220] leading-[1.05]">
        First-Time Buyer Guide: The UK Home Buying Process
      </h1>
      <p className="mt-5 text-[#475569] leading-relaxed text-base sm:text-lg max-w-2xl">
        A plain-English walkthrough of how buying a home in the UK actually works — from getting your finances ready
        through to picking up the keys. Use it to feel informed before any conversation with a broker, lender or solicitor.
      </p>
      <div className="mt-6 flex flex-wrap gap-2">
        {TOP_LINKS.map((t) => (
          <a
            key={t.id}
            href={`#${t.id}`}
            data-testid={`ftb-toplink-${t.id}`}
            className="inline-flex items-center gap-1.5 text-[11px] font-medium uppercase tracking-widest text-[#0F766E] bg-[#0F766E]/8 border border-[#0F766E]/15 rounded-full px-2.5 py-1 hover:bg-[#0F766E]/15 hover:border-[#0F766E]/30 transition-colors"
          >
            {t.label}
          </a>
        ))}
      </div>
      <GuideMeta lastReviewed="July 2026" readTime="15 min read" />
    </div>

    <div className="grid lg:grid-cols-4 gap-8 mt-12">
      <aside className="hidden lg:block lg:col-span-1">
        <div className="sticky top-24 mp-card p-5">
          <div className="text-xs uppercase tracking-widest text-[#0F766E] mb-3">On this page</div>
          <ul className="space-y-2 text-sm">
            {sections.map((s) => (
              <li key={s.id}>
                <a href={`#${s.id}`} className="text-[#475569] hover:text-[#0F766E] block leading-snug">
                  {s.h}
                </a>
              </li>
            ))}
          </ul>
        </div>
      </aside>

      <div className="lg:col-span-3 space-y-6">
        {sections.map((s) => <Section key={s.id} s={s} />)}

        <div className="grid sm:grid-cols-2 gap-4 pt-2">
          <Link to="/snapshot" className="mp-card p-6 mp-card-hover" data-testid="ftb-cta-snapshot">
            <div className="text-xs uppercase tracking-widest text-[#0F766E]">Next step</div>
            <div className="font-sora text-xl font-semibold mt-1">Start your free Mortgage Snapshot</div>
            <div className="text-sm text-[#475569] mt-1 inline-flex items-center gap-1">See the figures clearly <ArrowRight className="w-4 h-4" /></div>
          </Link>
          <Link to="/calculator" className="mp-card p-6 mp-card-hover" data-testid="ftb-cta-calc">
            <div className="text-xs uppercase tracking-widest text-[#0F766E]">Or estimate</div>
            <div className="font-sora text-xl font-semibold mt-1">Use the mortgage calculator</div>
            <div className="text-sm text-[#475569] mt-1 inline-flex items-center gap-1">Explore monthly repayments <ArrowRight className="w-4 h-4" /></div>
          </Link>
        </div>
        {cashbackPromotionEnabled && <CashbackPromptCard source="guide_ftb" />}
        <ComplianceStrip />
      </div>
    </div>
  </div>
);

export default FirstTimeBuyer;
